<template>
  <div class="tooltip-container" @mouseenter="showTooltip" @mouseleave="hideTooltip">
    <slot></slot>
    <Transition name="tooltip-fade">
      <div v-if="isVisible" class="tooltip-content" :style="tooltipStyle">
        {{ text }}
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

interface Props {
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  width?: string;
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top',
});

const isVisible = ref(false);

const showTooltip = () => {
  isVisible.value = true;
};

const hideTooltip = () => {
  isVisible.value = false;
};

const tooltipStyle = computed(() => {
  const positions = {
    top: {
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%) translateY(-8px)',
    },
    bottom: {
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%) translateY(8px)',
    },
    left: {
      right: '100%',
      top: '50%',
      transform: 'translateY(-50%) translateX(-8px)',
    },
    right: {
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%) translateX(8px)',
    },
  };
  return {
    ...positions[props.position],
    ...(props.width && { width: props.width, maxWidth: props.width }),
  };
});
</script>

<style scoped>
.tooltip-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tooltip-content {
  position: absolute;
  background-color: var(--ion-color-primary);
  color: var(--ion-text-color-contrast);
  padding: 0.5em 1em;
  border-radius: 8px;
  font-size: 0.875rem;
  white-space: normal;
  overflow-wrap: break-word;
  z-index: 1000;
  pointer-events: none;
  box-shadow: var(--ion-box-shadow);
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
